import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import Table from '../components/Table'
import Radom, { Deposit, ManagedBalanceLedger, ManagedEvent, ManagedEventWithRelatedCustomer, Payout, Transaction, TransactionKind, TransactionStatus, WithdrawalRequest, WithdrawalRequestWithAccountName, WithdrawalStatus } from '../state/Radom'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import { testNetworks, prodNetworks, getMethod } from '../util/Managed'
import { cutString, errorToast, roundAssetAmount, unifyLedger, useTestMode } from '../util/Util'
import Pagination from '../components/Pagination'
import Checkbox from '../components/Checkbox'
import PayoutTransactionLogo from '../images/PayoutTransaction.svg'
import WithdrawalTransactionLogo from '../images/WithdrawalTransaction.svg'
import PaymentTransactionLogo from '../images/PaymentTransaction.svg'
import DepositTransactionLogo from '../images/DepositTransaction.svg'
import TimeText, { TimeFormatType } from '../components/TimeText'
import styled from 'styled-components'
import { ManagedPaymentEvent, parseManagedEvent } from '../state/Events'
import { Chevron } from '../icons/Chevron'
import ExportTransactionsForm from '../components/ExportTransactionsForm'
import Modal from '../components/Modal'
import Dots from '../icons/Dots'
import PageHeader from '../components/PageHeader'
import _ from 'lodash'
import Spinner from '../components/Spinner'

export interface TransactionSummary {
  fee: {
    amount: string | number
    ticker: string
  }
  in: {
    amount: string | number
    ticker: string
    color: string
  }
  out?: {
    amount: string | number
    ticker: string
    color: string
  }
}

export interface ExtendedTransaction extends Transaction {
  status: TransactionStatus
  summary: TransactionSummary
  related: { url: string, name: string }
  url: string
}

export const TransactionStatusBadge = styled.div<{ status: TransactionStatus }>`
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
  width: fit-content;

  ${({ status }) => {
    let bgColor = RADOM_COLORS.ERROR_LIGHT as string
    let textColor = RADOM_COLORS.ERROR as string

    switch (status) {
    case TransactionStatus.Pending:
      bgColor = RADOM_COLORS.GRAY_MED as string
      textColor = RADOM_COLORS.GRAY_DARKEST as string
      break
    case TransactionStatus.Successful:
      bgColor = RADOM_COLORS.SUCCESS_LIGHT as string
      textColor = RADOM_COLORS.SUCCESS as string
      break
    case TransactionStatus.Incomplete:
      bgColor = RADOM_COLORS.WARNING_LIGHT as string
      textColor = RADOM_COLORS.WARNING as string
      break
    }

    return `
    background-color: ${bgColor};
    color: ${textColor};
  `
  }};
`

const TRANSACTION_ICONS = {
  [TransactionKind.Deposit]: DepositTransactionLogo,
  [TransactionKind.Withdrawal]: WithdrawalTransactionLogo,
  [TransactionKind.Payment]: PaymentTransactionLogo,
  [TransactionKind.Payout]: PayoutTransactionLogo
}

const Transactions = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()

  const pageSize = 20
  const [curPage, setCurPage] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [extendedTransactions, setExtendedTransactions] = useState<ExtendedTransaction[]>([])
  const [exportTransactionsModalOpen, setExportTransactionsModalOpen] = useState(false)
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>([])

  const [totalTransactions, setTotalTransactions] = useState<number>(0)

  const [transactionKinds, setTransactionKinds] = useState<TransactionKind[]>([
    TransactionKind.Deposit, TransactionKind.Payment, TransactionKind.Payout, TransactionKind.Withdrawal
  ])
  const [transactionStatuses, setTransactionStatuses] = useState<TransactionStatus[]>([
    TransactionStatus.Successful, TransactionStatus.Pending, TransactionStatus.Incomplete, TransactionStatus.Failed
  ])

  const fetchTransactions = async ({
    page,
    kinds,
    statuses,
    isTestMode
  }: {
    page: number
    kinds: TransactionKind[]
    statuses: TransactionStatus[]
    isTestMode: boolean
  }): Promise<void> => {
    setIsLoading(true)
    setTransactions([])
    let transactions: Transaction[]
    try {
      const data = await Radom.listTransactions(
        page * pageSize,
        pageSize,
        kinds,
        statuses,
        isTestMode ? testNetworks : prodNetworks,
        new Date(0).toISOString(),
        new Date().toISOString(),
        isTestMode
      )
      transactions = data.data
      setTransactions(data.data)
      setTotalTransactions(data.total)
    } catch {
      setIsLoading(false)
      errorToast('Failed to load transactions')
      return
    }

    try {
      if (transactions.length !== 0) {
        const ids = transactions.map(item => {
          if (item.kind === TransactionKind.Payment) {
            const managedPaymentEvent =
              parseManagedEvent(item.payload as ManagedEventWithRelatedCustomer) as ManagedPaymentEvent

            return managedPaymentEvent.associatedEntity.id
          }
          return item.id
        })
        const ledger = await Radom.listManagedBalanceLedger(ids)
        setManagedBalanceLedger(ledger)
      }
    } catch (e) {
      errorToast('Failed to load ledger')
    }

    setIsLoading(false)
  }

  const debouncedFetch = useMemo(() => _.debounce(fetchTransactions, 300), [])

  const getTransactionStatus = (transaction: Transaction): TransactionStatus => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      if (withdrawal.withdrawalStatus === WithdrawalStatus.Complete) {
        return TransactionStatus.Successful
      }
      if (withdrawal.withdrawalStatus === WithdrawalStatus.Failed) {
        return TransactionStatus.Failed
      }
      return TransactionStatus.Pending
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEvent
      if (payment.eventType === 'incompletePayment') {
        return TransactionStatus.Incomplete
      }
      return TransactionStatus.Successful
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      if (payout.status === 'Complete') {
        return TransactionStatus.Successful
      }
      return TransactionStatus.Pending
    }
    }
    return TransactionStatus.Successful
  }

  const getUrl = (transaction: Transaction): string => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      return `/withdrawal/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payment: {
      return `/payment/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payout: {
      return `/payout/${transaction.id}?source=transactions`
    }
    }
    return `/deposit/${transaction.id}?source=transactions`
  }

  const getTransactionSummary = (transaction: Transaction): TransactionSummary => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      const paymentMethod = getMethod(withdrawal.network, withdrawal.token)
      const isFiatWithdrawal = !!withdrawal.receipt?.FiatReceipt
      const fiat = withdrawal?.receipt?.FiatReceipt
      const finalAmount = fiat?.finalAmount
      const currency: 'EUR' | 'USD' = !fiat?.transferMethod || fiat.transferMethod.toLowerCase() === 'sepa' ? 'EUR' : 'USD'
      const fiatStartAmount = Math.round((Number(withdrawal.withdrawalAmount) / (1 - Number(fiat?.percentFee || '0'))) + Number(fiat?.flatFee || '0'))
      const amount = Number(isFiatWithdrawal ? fiatStartAmount.toString() : withdrawal.withdrawalAmount)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)
      const evm = withdrawal?.receipt?.EVMReceipt
      const dot = withdrawal?.receipt?.DOTReceipt
      const btc = withdrawal?.receipt?.BTCReceipt
      const sol = withdrawal?.receipt?.SOLReceipt
      const networkFeeAmount =
        evm?.networkFeeAmount ||
        dot?.networkFeeAmount ||
        btc?.networkFeeAmount ||
        sol?.networkFeeAmount
      const feeAmount = isFiatWithdrawal ? (fiatStartAmount - Number(withdrawal.withdrawalAmount)) : (networkFeeAmount || '0')
      return {
        fee: {
          amount: roundAssetAmount(paymentMethod.ticker, Number(feeAmount)),
          ticker: paymentMethod.ticker
        },
        in: {
          amount: `- ${amountString}`,
          ticker: paymentMethod.ticker,
          color: RADOM_COLORS.RED900
        },
        ...(isFiatWithdrawal && finalAmount
          ? {
            out: {
              amount: roundAssetAmount(currency, Number(finalAmount)),
              ticker: currency,
              color: RADOM_COLORS.RADOM_BLUE
            }
          }
          : {})
      }
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      const isFiatPayment = !!payment.amountsBreakdown?.onrampFee
      const feeTicker = isFiatPayment ? getMethod(payment.amountsBreakdown?.onrampNetwork || 'Ethereum', payment.amountsBreakdown?.onrampToken).ticker : managedPaymentEvent.paymentMethod.ticker
      const amount = Number(managedPaymentEvent.amount)
      const cryptoFee =
          payment.eventData?.payment?.paymentSummary?.feeAmount ??
          payment.eventData?.recurringPayment?.paymentSummary?.feeAmount ??
          payment.eventData?.subscriptionPayment?.paymentSummary?.feeAmount ??
          payment.eventData?.incompletePayment?.paymentSummary?.feeAmount ??
          '0'
      const cryptoFeeCurrency =
          payment.eventData?.payment?.paymentSummary?.currency ??
          payment.eventData?.recurringPayment?.paymentSummary?.currency ??
          payment.eventData?.subscriptionPayment?.paymentSummary?.currency ??
          payment.eventData?.incompletePayment?.paymentSummary?.currency ?? 'USD'
      const conversionRates = payment.eventData?.payment?.conversionRates ??
          payment.eventData?.recurringPayment?.conversionRates ??
          payment.eventData?.subscriptionPayment?.conversionRates ??
          payment.eventData?.incompletePayment?.conversionRates ?? []
      let finalCryptoFeeNumber: number = 0
      if (!isFiatPayment) {
        if (cryptoFeeCurrency === feeTicker) {
          finalCryptoFeeNumber = Number(cryptoFee)
        } else {
          const conversionRate = conversionRates.find(item => item.from === cryptoFeeCurrency && item.to === feeTicker)
          finalCryptoFeeNumber = Number(cryptoFee) * Number(conversionRate?.rate ?? '1')
        }
      }
      const amountString = roundAssetAmount(managedPaymentEvent.paymentMethod.ticker, (amount + finalCryptoFeeNumber))
      const outPaymentMethod = getMethod(payment.amountsBreakdown?.onrampNetwork || 'Ethereum', payment.amountsBreakdown?.onrampToken)
      const outAmount = Number(payment.amountsBreakdown?.onrampGrossAmount ?? '0')
      const outAmountString = roundAssetAmount(outPaymentMethod.ticker, outAmount)
      const feeAmountNumber = isFiatPayment ? Number(payment.amountsBreakdown?.onrampFee ?? '0') : finalCryptoFeeNumber

      return {
        fee: {
          amount: roundAssetAmount(feeTicker, feeAmountNumber),
          ticker: feeTicker
        },
        in: {
          amount: isFiatPayment ? amountString : `+ ${amountString}`,
          ticker: managedPaymentEvent.paymentMethod.ticker,
          color: isFiatPayment ? RADOM_COLORS.RADOM_BLUE : RADOM_COLORS.NICE_GREEN
        },
        ...(isFiatPayment
          ? {
            out: {
              amount: `+ ${outAmountString}`,
              ticker: outPaymentMethod.ticker,
              color: RADOM_COLORS.NICE_GREEN
            }
          }
          : {})
      }
    }
    case TransactionKind.Payout: { // todo: adjust when fiat payouts
      const payout = transaction.payload as Payout
      const paymentMethod = getMethod(
        payout.payouts[0].payoutType.crypto?.network || '',
        payout.payouts[0].payoutType.crypto?.token)
      const amount = Number(payout.totalPayoutUsd)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)

      return {
        fee: {
          amount: roundAssetAmount(paymentMethod.ticker, Number(payout.estimatedFeeUsd)),
          ticker: paymentMethod.ticker
        },
        in: {
          amount: `- ${amountString}`,
          ticker: paymentMethod.ticker,
          color: RADOM_COLORS.RED900
        }
      }
    }
    }
    const deposit = transaction.payload as Deposit
    if (deposit.depositMethod.Crypto) {
      const paymentMethod = getMethod(
        deposit.depositMethod.Crypto.network,
        deposit.depositMethod.Crypto.token)
      const amount = Number(deposit.grossAmount)
      const amountString = roundAssetAmount(paymentMethod.ticker, amount)

      return {
        fee: {
          amount: roundAssetAmount(paymentMethod.ticker, Number(deposit.fee)),
          ticker: paymentMethod.ticker
        },
        in: {
          amount: `+ ${amountString}`,
          ticker: paymentMethod.ticker,
          color: RADOM_COLORS.NICE_GREEN
        }
      }
    }
    const paymentMethodOut = getMethod(
      deposit.depositMethod.Fiat?.onrampNetwork || '',
      deposit.depositMethod.Fiat?.onrampToken)
    const paymentMethodIn = getMethod(
      'Fiat',
      deposit.depositMethod.Fiat?.fiatCurrency)
    const amount = Number(deposit.grossAmount)
    const amountString = roundAssetAmount(paymentMethodOut.ticker, amount)
    return {
      fee: {
        amount: roundAssetAmount(paymentMethodOut.ticker, Number(deposit.fee)),
        ticker: paymentMethodOut.ticker
      },
      in: {
        amount: roundAssetAmount(paymentMethodIn.ticker, Number(deposit.fiatGrossAmount ?? '0')),
        ticker: paymentMethodIn.ticker,
        color: RADOM_COLORS.RADOM_BLUE
      },
      out: {
        amount: `+ ${amountString}`,
        ticker: paymentMethodOut.ticker,
        color: RADOM_COLORS.NICE_GREEN
      }
    }
  }

  const getTransactionRelated = (transaction: Transaction): { name: string, url: string } => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequestWithAccountName
      const text = withdrawal.withdrawalAccountName ?? withdrawal.withdrawalAddress
      return { name: text.length > 8 ? cutString(text, 8) : text, url: '/accounts' }
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEventWithRelatedCustomer
      const text = payment.relatedCustomer?.name ?? payment.relatedCustomer?.email ?? ''
      return { name: text.length > 8 ? cutString(text, 8) : text, url: payment.relatedCustomer?.id ? `/customer/${payment.relatedCustomer?.id}` : '' }
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const tail = payout.payouts.length > 1 ? 'Recipients' : 'Receipient'
      return { name: `${payout.payouts.length} ${tail}`, url: '' }
    }
    }
    const deposit = transaction.payload as Deposit
    const text = deposit.depositAccountName
    return { name: text.length > 8 ? cutString(text, 8) : text, url: '/deposit_addresses' }
  }

  useEffect(() => {
    debouncedFetch({
      page: curPage,
      kinds: transactionKinds,
      statuses: transactionStatuses,
      isTestMode: testMode
    })
    return () => debouncedFetch.cancel()
  }, [curPage, transactionKinds, transactionStatuses, testMode, debouncedFetch])

  useEffect(() => {
    setCurPage(0)
  }, [testMode, transactionKinds, transactionStatuses])

  useEffect(() => {
    setExtendedTransactions(
      transactions.map((item) =>
        ({
          ...item,
          status: getTransactionStatus(item),
          summary: getTransactionSummary(item),
          url: getUrl(item),
          related: getTransactionRelated(item)
        }))
    )
  }, [transactions])

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 900)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return <div style={{ display: 'flex', width: '100%', padding: '30px 48px', flexDirection: 'column', gap: '20px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <PageHeader title='Transactions' subTitle='Manage your transactions'/>
      <Dropdown
        noChevron
        selectedContent={<Dots width={15} />}
        dropdownContent={
          <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            <DropdownItem onClick={() => setExportTransactionsModalOpen(true)}>Export transactions</DropdownItem>
          </div>
        }
      />
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <Dropdown
        selectedContent={<div style={{ fontSize: '14px' }}>Status</div>}
        dropdownContent={
          <div style={{ position: 'relative' }}>
            {isLoading && <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.6)',
                zIndex: 10
              }}
            >
              <Spinner />
            </div>}
            {Object.values(TransactionStatus).map((s) => (
              <DropdownItem
                key={s}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                onClick={() => {
                  if (isLoading) return
                  setTransactionStatuses((prev) =>
                    prev.includes(s)
                      ? prev.filter((x) => x !== s)
                      : [...prev, s])
                }}
              >
                <Checkbox size={16} checked={transactionStatuses.includes(s)} />
                <span>{s}</span>
              </DropdownItem>
            ))}
          </div>
        }
      />
      <Dropdown
        selectedContent={<div style={{ fontSize: '14px' }}>Type</div>}
        dropdownContent={
          <div style={{ position: 'relative' }}>
            {isLoading && <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.6)',
                zIndex: 10
              }}
            >
              <Spinner />
            </div>}
            {Object.values(TransactionKind).map((k) => (
              <DropdownItem
                key={k}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                onClick={() => {
                  if (isLoading) return
                  setTransactionKinds((prev) =>
                    prev.includes(k)
                      ? prev.filter((x) => x !== k)
                      : [...prev, k]
                  )
                }}
              >
                <Checkbox size={16} checked={transactionKinds.includes(k)} />
                <span>{k}</span>
              </DropdownItem>
            ))}
          </div>
        }
      />
    </div>

    <Table
      borderless
      disableOnClickState
      headers={['Type', 'Status', 'Related', 'Amount', ...(!isMobile ? ['Fee', 'Balance', 'Date'] : [])].map(h => <span key={h}>{h}</span>)}
      onClickRow={i => {
        const s = extendedTransactions[i]
        navigate(s.url)
      }}
      isLoading={isLoading}
      items={extendedTransactions.map(s => {
        const ledger = managedBalanceLedger.filter(item => {
          if (s.kind !== TransactionKind.Payment) { return item.associatedId === s.id }
          const managedPaymentEvent =
                  parseManagedEvent(s.payload as ManagedEventWithRelatedCustomer) as ManagedPaymentEvent
          return managedPaymentEvent.associatedEntity.id === item.associatedId
        }).filter(item => {
          const balancePaymentMethod = getMethod(item.network, item.token)
          let transactionCurrencyTicker: string
          if (s.kind === TransactionKind.Deposit || s.kind === TransactionKind.Payment) {
            if (s.summary.out) {
              transactionCurrencyTicker = s.summary.out.ticker
            } else {
              transactionCurrencyTicker = s.summary.in.ticker
            }
          } else {
            transactionCurrencyTicker = s.summary.in.ticker
          }
          return transactionCurrencyTicker === balancePaymentMethod.ticker
        })
        const unified = unifyLedger(ledger)
        return [
          <div key={'1'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <img src={TRANSACTION_ICONS[s.kind] as string} style={{ width: 20, height: 20 }} />
            <span>{s.kind}</span>
          </div>,
          <div key={'2'}>
            <TransactionStatusBadge status={s.status}>{s.status}</TransactionStatusBadge>
          </div>,
          <div key={'3'} onClick={(e) => e.stopPropagation()}>
            {s.related.url.length > 0
              ? <Link to={s.related.url} style={{ color: RADOM_COLORS.BLACK }}>
                {s.related.name}
              </Link>
              : <span>{s.related.name}</span>}
          </div>,
          <div key={'4'} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ color: s.summary.in.color }}>
                {s.summary.in.amount}
              </span>
              <span style={{ color: s.summary.in.color }}>{s.summary.in.ticker}</span>
            </div>
            {
              s.summary.out && <>
                <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: s.summary.out.color }}>
                    {s.summary.out.amount}
                  </span>
                  <span style={{ color: s.summary.out.color }}>{s.summary.out.ticker}</span>
                </div>
              </>
            }
          </div>,
          ...(!isMobile
            ? [<div key={'5'}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span>
                  {s.summary.fee.amount}
                </span>
                <span style={{ color: RADOM_COLORS.GRAY4 }}>{s.summary.fee.ticker}</span>
              </div>
            </div>,
            <div key={'6'} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {unified.map((balanceEntry, idx) => {
                const paymentMethod = getMethod(balanceEntry.network, balanceEntry.token)

                return <div key={idx} style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
                  <span>
                    {roundAssetAmount(paymentMethod.ticker, Number(balanceEntry.balanceAfter))}
                  </span>
                  <span style={{ color: RADOM_COLORS.GRAY4 }}>{paymentMethod.ticker} on {balanceEntry.network}</span>
                </div>
              }
              )}
            </div>,
            <div key={'7'}>
              <TimeText textColor={RADOM_COLORS.BLACK} date={new Date(s.createdAt)}
                formatModes={[TimeFormatType.ShortUTCJustDay]} />
            </div>]
            : [])
        ]
      })}
      emptyPlaceholderText='No transactions yet'
    />
    {
      !isLoading && <Pagination
        curPage={curPage}
        numPages={Math.ceil(totalTransactions / pageSize)}
        onPageClick={page => setCurPage(page)}
      />
    }
    {
      exportTransactionsModalOpen &&
      <Modal visible={exportTransactionsModalOpen} onClose={() => setExportTransactionsModalOpen(false)} title='Export transactions'>
        <ExportTransactionsForm />
      </Modal>
    }
  </div>
})

export default Transactions
